import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import i18next from 'i18next';
import { IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import {
  IResponseHandlerOptions,
  useResponseHandler,
} from 'utils/service/responseHandler';
import {
  IContractBulkUpdate,
  IContractListItemResponse,
  IContractListParams,
  IContractListResponse,
  IContractResponse,
  IContractsToExpire,
  ICreateContract,
  ICreateContractFromTemplate,
  IUpdateContract,
} from './types';

interface IContractsByLabelOptions {
  params?: IContractListParams;
  enabled?: boolean;
  retry?: boolean;
  staleTime?: number; // 5 minutes (data stays fresh)
  refetchOnWindowFocus?: boolean; // Don't refetch when window gains focus
}

export const useGetContractsByLabel = (
  labelId: string,
  options?: IContractsByLabelOptions
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    options?.params?.page,
    options?.params?.itemCount
  );

  return useInfiniteQuery<IContractListItemResponse[], DefaultError>({
    queryKey: ['contacts', labelId, options?.params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: `Contract/label`,
        params: {
          ...options?.params,
          pageCount: itemCount,
          page: pageParam,
          labelId: labelId,
        },
      });

      const contracts = await responseHandler<IContractListResponse>(response, {
        groupName: `useGetContactsListInfinite - ${pageParam}`,
      });

      return infiniteHandler<IContractListItemResponse>(contracts);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
    enabled: options?.enabled,
    retry: options?.retry,
    staleTime: options?.staleTime,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
  });
};

export const useGetContractsToExpire = (params?: IContractListParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<IContractsToExpire[], DefaultError>({
    queryKey: ['contacts-to-expire', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: `Contract/GetExpiringContracts`,
        params: { ...params, itemCount, page: pageParam },
      });

      const contracts = await responseHandler<
        IInfinitePageResponse<IContractsToExpire>
      >(response, {
        groupName: `useGetContractsToExpire - ${pageParam}`,
      });

      return infiniteHandler<IContractsToExpire>(contracts);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

// export const useGetContractsToExpire = () => {
//   const { responseHandler } = useResponseHandler();
//   return useQuery<IContractsToExpire[], DefaultError>({
//     queryKey: ['contracts-to-expire'],
//     async queryFn() {
//       const response = await apiClient({
//         method: 'GET',
//         url: `Contract/GetExpiringContracts`,
//       });

//       return responseHandler(response, {
//         groupName: `useGetContractsToExpire`,
//       });
//     },
//   });
// };

export const useGetContracts = (params?: IContractListParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<IContractListItemResponse[], DefaultError>({
    queryKey: ['contacts', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: `Contract/Search`,
        params: { ...params, itemCount, page: pageParam },
      });

      const contracts = await responseHandler<IContractListResponse>(response, {
        groupName: `useGetContactsListInfinite - ${pageParam}`,
      });

      return infiniteHandler<IContractListItemResponse>(contracts);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetContract = (
  id: string,
  options?: IResponseHandlerOptions<IContractResponse>,
  enabled?: boolean,
  retry?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IContractResponse, DefaultError>({
    queryKey: ['contract', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Contract/${id}`,
      });

      const data = await responseHandler(response, {
        groupName: `useGetContract - ${id}`,
        notFoundPath: '/templates',
        ...options,
      });

      return data;
    },
    retry,
    enabled,
  });
};

export const useCreateContractForLabel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('createContractForLabel');

  return useMutation<IContractResponse, DefaultError, ICreateContract>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.create.loading', {
          what: body.title,
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `Contract`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `createContractForLabel`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: data.title,
        })
      );
    },
    onError: dismiss,
  });
};
export const useCreateContractFromTemplateForLabel = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useCreateContractFromTemplateForLabel'
  );

  return useMutation<
    IContractResponse,
    DefaultError,
    ICreateContractFromTemplate
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.create.loading', {
          what: i18next.t('contract-template.contract.what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `Contract/template`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `useCreateContractFromTemplateForLabel`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('contract-template.contract.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useUpdateContract = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('updateContract');

  return useMutation<unknown, DefaultError, IUpdateContract>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.update.loading', {
          what: body.title,
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `Contract`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `updateContract`,
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('contract-template.contract.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useActivateContract = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('activateContract');

  return useMutation<unknown, DefaultError, string>({
    mutationFn: async contractId => {
      loading(
        i18next.t('service-code.active.loading', {
          what: i18next.t('contract-template.contract.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `Contract/activate`,
        body: { contractId },
      });

      return responseHandler(response, {
        groupName: `activateContract`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t('service-message.active.success', {
          what: i18next.t('contract-template.contract.what'),
        })
      );
    },
    onError: dismiss,
  });
};
export const useWithdrawContract = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('withdrawContract');

  return useMutation<unknown, DefaultError, string>({
    mutationFn: async contractId => {
      loading(
        i18next.t('service-code.withdraw.loading', {
          what: i18next.t('contract-template.contract.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `Contract/withdraw`,
        body: { contractId },
      });

      return responseHandler(response, {
        groupName: `withdrawContract`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t('service-message.withdraw.success', {
          what: i18next.t('contract-template.contract.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteContract = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('deleteContract');

  return useMutation<unknown, DefaultError, string>({
    mutationFn: async id => {
      loading(
        i18next.t('service-code.remove.loading', {
          what: i18next.t('contract-template.contract.what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Contract/${id}`,
      });

      return responseHandler(response, {
        groupName: `deleteContract`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('contract-template.contract.what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useBulkUpdateContract = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useBulkUpdateContract');

  return useMutation<unknown, DefaultError, IContractBulkUpdate>({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.update.loading', {
          what: i18next.t('contract-template.contract.what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `Contract/BulkUpdate`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `useBulkUpdateContract`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('contract-template.contract.what'),
        })
      );
    },
    onError: dismiss,
  });
};
